<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('external_coach_billing.title') }}
        </h6>
      </div>

      <div class="element-box">
        <Menu :countUnreadMessageForCoordinator="countUnreadMessageForCoordinator"/>

        <Search
            :filters="filters"
            :loading="isSearching"
            :coachDropdown="coachDropdown"
            :statusDropdown="statusDropdown"
            @search="getExistedCoachBills"/>

        <PerPage
            class="mt-3"
            :values="[10, 25, 50, 100]"
            @update:perPage="getExistedCoachBills"/>

        <form @submit.prevent="" method="post">
          <div class="table-responsive overflow-auto pt-4">
            <table class="table table-striped">
              <thead>
              <tr>
                <th style="width: 3%;"></th>
                <th>Rechnungs-Nr.</th>
                <th style="width: 20%;">Coach</th>
                <th>Kreditor</th>
                <th>Rechnungsdatum</th>
                <th class="text-right"># Sitzungen</th>
                <th class="text-right">Rechnungsbetrag</th>
                <th class="text-left"><span class="pl-5">Status</span></th>
                <th style="width: 5%;"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(coachBill) in coachBills.data" :key="coachBill.id">
                <td class="text-center">
                  <input type="checkbox" v-model="selectedBillIds" :value="coachBill.id"/>
                </td>
                <td>GS {{ coachBill?.billNumber }}</td>
                <td>{{ coachBill?.coach?.firstName }} {{ coachBill?.coach?.lastName }}</td>
                <td>{{ coachBill?.coach?.creditor}}</td>
                <td>{{ coachBill.createdAt }}</td>
                <td class="text-right">{{ coachBill.totalUnits }}</td>
                <td class="text-right">{{ coachBill.sum }}</td>
                <td class="text-left" style="min-width: 160px">
                  <div class="pl-3">
                    <div v-if="coachBill.status === 'billed' || coachBill.status === 'approved'"
                         class="status-pill green p-2 mr-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    <div v-else-if="coachBill.status === 'pending'"
                         class="status-pill yellow p-2 mr-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    <div v-else
                         class="status-pill red p-2 mr-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    {{ coachBill.statusTranslation }}
                  </div>
                </td>
                <td class="text-right">
                  <PrintButton :coachBill="coachBill"/>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div class="row mt-3 d-flex align-items-end">
            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-start pl-4 mb-4">
              <button type="submit"
                      @click.prevent="downloadBills"
                      class="btn btn-primary">Rechnung download
              </button>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-end">
              <Pagination
                  v-if="coachBills?.pagination?.total_pages > 1"
                  :totalPages="coachBills?.pagination?.total_pages"
                  :currentPage="coachBills?.pagination?.current_page"
                  :totalItems="coachBills?.pagination?.total_items"
                  :perPage="coachBills?.pagination?.items_per_page"
                  @pagechanged="getExistedCoachBills"/>
            </div>
          </div>
        </form>

      </div>
    </div>

  </PageTemplate>
</template>


<script setup>
import coachBillingService from '@/api/CoachBilling';
import CreateButton from '@/components/CreateButton.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import { getExternalCoachForCoordinatorDropdown } from '@/shared/dropdowns';
import { downloadFile } from '@/shared/functions';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import PrintButton from '@/views/App/CoachBilling/Accountant/Existing/PrintButton.vue';
import Search from '@/views/App/CoachBilling/Coordinator/ExistingBills/Search.vue';
import Menu from '@/views/App/CoachBilling/Coordinator/Menu.vue';
import { computed, onMounted, ref } from 'vue';

const piniaEnumsState                  = piniaEnumsStore();
const getEnums                         = computed(() => piniaEnumsState.enums);
const coachBills                       = ref([]);
const coachDropdown                    = ref([]);
const statusDropdown                   = ref([]);
const selectedBillIds                  = ref([]);
const isSearching                      = ref(false);
const isLoaded                         = ref(false);
const countUnreadMessageForCoordinator = ref(0);

const filters = ref({
  coachId: '',
  billNumber: '',
  startDate: '',
  endDate: '',
  withMessage: false,
});

async function getExistedCoachBills(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachBills.value?.pagination?.items_per_page;

  try {
    const { data } = await coachBillingService.coordinatorExistedBills({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachBills.value  = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function downloadBills() {
  CloseToast();

  try {
    await downloadFile('/coach-billing/download-bills', undefined, { billIds: selectedBillIds.value }, false, 'POST');
    selectedBillIds.value = [];
  } catch (error) {
    await ErrorToast(error);
  }
}

async function countUnreadMessages() {
  try {
    const { data } = await coachBillingService.coordinatorCountUnreadMessages();

    countUnreadMessageForCoordinator.value = data.data.count;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getStatusDropdown() {
  statusDropdown.value = getEnums?.value?.billStatuses.filter(status => status.value === 'billed' || status.value === 'canceled') || [];
}

onMounted(async () => {
  await Promise.all([
    getExistedCoachBills(),
    getStatusDropdown(),
    getExternalCoachForCoordinatorDropdown(coachDropdown),
    countUnreadMessages(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>